import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {NgClass, NgFor, NgIf, NgStyle} from '@angular/common';
import {
  CardQuestionnaireOptionDto,
  InteractionAnswerSaveDto,
  InteractionCardDto,
  InteractionsCardItemDto,
  ResultsDto, ResultsItemDto, StatisticsDto
} from '../../models/card.model';
import {CardButtonComponent} from '../card-button/card-button.component';
import {CardResultsComponent} from '../card-results/card-results.component';
import {ConferenceService} from '../../service/conference.service';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-card-questionnaire',
  templateUrl: './card-questionnaire.component.html',
  styleUrls: ['./card-questionnaire.component.scss'],
  standalone: true,
  imports: [NgStyle, NgClass, NgIf, NgFor, CardButtonComponent, CardResultsComponent, TranslateModule],
})
export class CardQuestionnaireComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<InteractionAnswerSaveDto>();

  @Input() set card(card: InteractionCardDto) {
    this._card = card;
    this.isSubmitted = (card.cardAnswer && card.cardAnswer.isCompleted);

    this.initData();
    this.switchViews();
  }

  @Input() set isResults(isResults: boolean) {
    this._isResults = isResults;
    this.switchViews();
  }

  type: string = 'poll'; // quiz, poll
  isMultiselect: boolean = false; // optional
  minSelect: number = 0; // optional
  maxSelect: number = 0; // optional
  comment: string | undefined; // optional
  color: string = '#FFFFFF'; // optional

  isEnableSwitchViews: boolean = false;
  isSubmitted: boolean = false;

  _isResults: boolean | null = null;
  isShowingResults: boolean | null = null;

  _card: InteractionCardDto = {} as InteractionCardDto;
  options: CardQuestionnaireOptionDto[] = [];
  results: ResultsDto | null = null;

  constructor(
    private conferenceService: ConferenceService,
  ) {
  }

  ngOnInit() {
  }

  initData() {
    this.type = this._card.type;
    this.isMultiselect = this._card.isMultiselect;
    this.minSelect = this._card.minSelect;
    this.maxSelect = this._card.maxSelect;
    this.comment = this._card.comment;
    this.color = this._card.color;
    if (this._card.interactionsCardItems && this._card.interactionsCardItems.length) {
      this.options = this._card.interactionsCardItems.map(item => this.extractInteractionAnswer(item));
    }
  }

  choose(option: CardQuestionnaireOptionDto) {
    if (this.isSubmitted) {
      return;
    }

    if (!this.isMultiselect) {
      this.options.forEach(o => o.isSelected = false);
    } else {
      if (!option.isSelected && this.maxSelect > 0 && this.selectedOptionsCount() >= this.maxSelect) {
        return;
      }
    }
    option.isSelected = !option.isSelected;
  }

  selectedOptions() {
    return this.options.filter(o => o.isSelected);
  }

  selectedOptionsCount() {
    return this.selectedOptions().length;
  }

  isValid() {
    if (this.minSelect > 0 && this.selectedOptionsCount() < this.minSelect
      || this.maxSelect > 0 && this.selectedOptionsCount() > this.maxSelect) {
      return false;
    }

    return true;
  }

  isPassed() {
    return this.options.every(o => o.isSelected === !!o.item.isValid);
  }

  submit() {
    if (!this.isValid()) {
      return;
    }

    this.isSubmitted = true;
    this.onSubmit.emit({
      interactionCardId: this._card.id,
      score: this._card.score,
      isCompleted: true,
      isPassed: this._card.isNoCorrect || this.isPassed(),
      groupExtId: 0,
      text: '',
      InteractionAnswerItems: this.options.map(o => ({
        interactionsCardItemId: o.item.id,
        value: o.isSelected ? 1 : 0,
        score: o.item.score,
      })),
    } as InteractionAnswerSaveDto);
  }

  private extractInteractionAnswer(item: any) {
    return {
      isSelected: item.cardItemAnswer && item.cardItemAnswer.value > 0 ? true : false,
      value: item.cardItemAnswer && item.cardItemAnswer.value > 0 ? item.cardItemAnswer.value : 0,
      item
    };
  }

  switchViews() {
    if (this._isResults === null) {
      return;
    }

    if (this._isResults) {
      this.showResults();
    } else {
      this.hideResults();
    }
  }

  showResults() {
    this.conferenceService.getStatistics(this._card.id, 0)
      .then((statistics) => {
        console.log('statistics', statistics);

        this.results = {
          items: this.options.map((option) => {
            const percent = this.getPercents(option.item.id, statistics);
            return {
              value: percent / 100,
              label: percent.toFixed(0) + '%',
              title: option.item.text,
              status: this.getResultsItemStyle(option.item),
            } as ResultsItemDto;
          })
        };

        this.isShowingResults = true;
      });
  }

  hideResults() {
    console.log('hideResults');
    this.isShowingResults = false;
  }

  getResultsItemStyle(item: InteractionsCardItemDto) {
    if (this._card.isNoCorrect) {
      return 'neutral';
    }

    if (this.type == 'quiz') {
      if (item.isValid) {
        return 'positive';
      }

      return 'negative';
    }

    return 'neutral';
  }

  getPercents(itemId: string, statistics: StatisticsDto) {
    if (statistics && statistics.interactionCardAnswerItems && statistics.interactionCardAnswerItems.length > 0) {
      const item = statistics.interactionCardAnswerItems.find((item) => item.interactionsCardItemId === itemId);
      const total = statistics.interactionCardAnswersCount;
      return total > 0 && item ? item.InteractionAnswerItemsCount / total * 100 : 0;
    }
    return 0;
  }
}
